import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import {
  getAccountingEntries,
  getAccountingEntryClasses,
  getAccountingEntryTypes,
  getAccountingNodes,
  getAccountingPeriods,
  getCostCenters,
} from 'lib/api/accounting';
import GlobalSearcher from 'lib/common/GlobalSearcher';
import CustomAlert from 'lib/designSystem/Alert';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import { hasAnyPermissions, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../services/redux/authSlice';
import { useAccountingPeriod } from '../../context/AccountingPeriodContext';
import {
  AccountingEntry,
  AccountingEntryClass,
  AccountingNode,
  AccountingPeriod,
  CostCenter,
  EntryType,
} from '../../types';
import { toEndOfDayISO } from '../utils/fetchWithDateChunking';
import EditEntry from './components/EntryWithLines/EditEntry';
import BatchImporter from './components/Importer';
import AccountingEntriesTable from './components/Table';

const AccountingEntries: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [data, setData] = useState<AccountingEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [nodes, setNodes] = useState<AccountingNode[]>([]);
  const [entryClasses, setEntryClasses] = useState<AccountingEntryClass[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<
    AccountingPeriod[]
  >([]);
  const { startDate, endDate, setPeriod } = useAccountingPeriod();
  const [accountingEntryNumber, setAccountingEntryNumber] =
    useState<string>('');
  const [businessIdentifier, setBusinessIdentifier] = useState<string>('');
  const [businessName, setBusinessName] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [openImporter, setOpenImporter] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [entry, setEntry] = useState<AccountingEntry>({
    name: '',
    lines: [],
    date: new Date().toISOString().split('T')[0],
    typeId: 'free',
    classId: '',
    companyId: 0,
    status: 'CLOSED',
  });
  const [successText, setSuccessText] = useState<string | null>(null);
  const [failedText, setFailedText] = useState<string | null>(null);

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (startDate != null) params.append('startDate', startDate);
    if (endDate != null) params.append('endDate', toEndOfDayISO(endDate));
    if (accountingEntryNumber)
      params.append('accountingEntryNumber', accountingEntryNumber);
    if (businessIdentifier)
      params.append('businessIdentifier', businessIdentifier);
    if (businessName) params.append('businessName', businessName);
    return params.toString();
  };

  const validateSomeFilter = () => {
    return (
      (startDate != null && startDate !== '') ||
      (endDate != null && endDate !== '') ||
      accountingEntryNumber ||
      businessIdentifier ||
      businessName
    );
  };
  const setUp = async (entryId?: number) => {
    const queryParams = buildQueryParams();
    if (!validateSomeFilter()) {
      return showAlert(
        dispatch,
        'warning',
        'Debe seleccionar al menos un filtro para buscar asientos.',
      );
    }
    try {
      setLoading(true);
      const response = await getAccountingEntries(dispatch, queryParams);
      setData(response);
      if (entryId) {
        const entrySetUp = response.find(
          (entry: AccountingEntry) => entry.id === entryId,
        );
        if (!entrySetUp) {
          setOpen(false);
          return setLoading(false);
        }

        const dateFormatted = entrySetUp.date.split('T')[0];
        setEntry({
          ...entrySetUp,
          date: dateFormatted,
        });
        setOpen(true);
      }
    } catch (error) {
      console.error('Error al cargar asientos:', error);
    }
    setLoading(false);
  };

  const setUpEntryEditing = async () => {
    try {
      const response = await getAccountingEntryTypes(dispatch);
      const responseNodes = await getAccountingNodes(dispatch);
      const responseEntryClasses = await getAccountingEntryClasses(dispatch);
      const responseCostCenters = await getCostCenters(dispatch);
      const responsePeriods = await getAccountingPeriods(dispatch);

      setEntryTypes(response);
      setNodes(responseNodes);
      setEntryClasses(responseEntryClasses);
      setCostCenters(responseCostCenters);
      setAccountingPeriods(responsePeriods);
    } catch (error) {
      console.error('Error al cargar datos para editar asientos:', error);
      setFailedText(
        'Error al cargar los datos necesarios para editar asientos.',
      );
    }
  };

  const handleNewEntry = () => {
    setOpen(true);
    setEntry({
      name: '',
      lines: [],
      date: new Date().toISOString().split('T')[0],
      typeId: 'free',
      classId: '',
      companyId: 0,
      status: 'CLOSED',
    });
  };

  const handleEditEntry = (entryId: number) => {
    const entry = data.find((entry: AccountingEntry) => entry.id === entryId);
    if (!entry) return alert('No se encontró la entrada.');

    const dateFormatted = entry.date.split('T')[0];
    setEntry({
      ...entry,
      date: dateFormatted,
    });
    setOpen(true);
  };

  useEffect(() => {
    void setUp();
    void setUpEntryEditing();
  }, []);

  const periodTitle =
    startDate != null && endDate != null
      ? `Período: ${dayjs(startDate).format('DD/MM/YYYY')} - ${dayjs(endDate).format('DD/MM/YYYY')}`
      : 'Asientos contables';

  const headerButtons: CustomButtonProps[] = [
    {
      customButton: (
        <GlobalSearcher
          filters={[
            {
              type: 'monthly',
              label: 'Período mensual',
              dateKey: 'startDate',
              value: startDate ?? '',
              setter: (val) => setPeriod(val || null, endDate),
            },
            {
              type: 'monthly',
              label: 'Período mensual',
              dateKey: 'endDate',
              value: endDate ?? '',
              setter: (val) => setPeriod(startDate, val || null),
            },
            {
              type: 'text',
              label: 'ID comprobante',
              value: accountingEntryNumber,
              setter: setAccountingEntryNumber,
            },
          ]}
          triggerSearch={async () => setUp()}
          shortcuts={true}
        />
      ),
      color: 'primary',
      action: () => {},
    },
    {
      name: 'Crear',
      color: 'primary',
      action: () => handleNewEntry(),
      disabled: !hasAnyPermissions(user, ['accounting_write_entries']),
    },
    {
      name: 'Importar',
      color: 'primary',
      action: () => setOpenImporter(true),
      disabled: !hasAnyPermissions(user, ['accounting_write_entries']),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <HeaderWithActions
        title={periodTitle}
        icon={<CalendarMonthIcon />}
        buttons={headerButtons}
      />
      <div>
        <AccountingEntriesTable
          entries={data}
          handleEditEntry={handleEditEntry}
          nodes={nodes}
          costCenters={costCenters}
        />
      </div>
      {open && (
        <EditEntry
          entry={entry}
          setEntry={setEntry}
          open={open}
          setOpen={setOpen}
          setUp={setUp}
          setSuccessText={setSuccessText}
          setFailedText={setFailedText}
          entryTypes={entryTypes}
          nodes={nodes}
          entryClasses={entryClasses}
          accountingPeriods={accountingPeriods}
        />
      )}
      <CustomAlert
        open={!!successText}
        onClose={() => setSuccessText(null)}
        severity="success"
        text={successText}
      />
      <CustomAlert
        open={!!failedText}
        onClose={() => setFailedText(null)}
        severity="error"
        text={failedText}
      />
      <BatchImporter
        open={openImporter}
        setOpen={setOpenImporter}
        nodes={nodes}
        entryClasses={entryClasses}
        accountingPeriods={accountingPeriods}
        companyId={user?.companyId || 0}
        onSuccess={() => void setUp()}
      />
    </div>
  );
};

export default AccountingEntries;
